import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Calendar from '../../components/Calendar/Calendar';
import { fetchCalendarTasks } from '../../services/TaskService';
import { getCurrentUser } from '../../services/authService';
import { fetchAllSystemUsers } from '../../services/TaskService';
import ActiveInactiveSwitch from '../../components/ActiveInactiveSwitch/ActiveInactiveSwitch';
import { getAppointmentRange } from 'services/scheduleServices';
import CalendarItem from 'components/Calendar/CalendarItem/CalendarItem';
import CalendarSidebar from 'components/Calendar/CalendarSidebar/CalendarSidebar';
import './calender.scss';
import usePermissionsCalendar from 'permissions/modules/calendar/usePermissionsCalendar';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { calendarI18n } from './i18n';

const buildDateKey = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const mapPriority = (priority) => {
  if (
    priority === 1 ||
    priority === 'Alta' ||
    priority === 'ALTA' ||
    priority === 'High'
  ) {
    return 'high';
  }

  if (
    priority === 2 ||
    priority === 'Media' ||
    priority === 'MEDIA' ||
    priority === 'Medium'
  ) {
    return 'medium';
  }

  if (
    priority === 3 ||
    priority === 'Baja' ||
    priority === 'BAJA' ||
    priority === 'Low'
  ) {
    return 'low';
  }

  return null;
};

const CalendarView = ({ DesignSystemComponents }) => {
  const { Card, ComboBox, SvgIcon, Button } = DesignSystemComponents;

  const { language } = useLanguage();
  const calendarLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: calendarI18n });

  const location = useLocation();

  const currentUser = getCurrentUser?.();
  const currentUserId =
    currentUser?.id ??
    currentUser?.userId ??
    currentUser?.usuarioAsignadoId ??
    null;
  const normalizedUserId =
    currentUserId !== null && currentUserId !== undefined
      ? Number(currentUserId)
      : null;

  const canSelectUser = true;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInactiveCases, setShowInactiveCases] = useState(false);
  const [showBajaCases, setShowBajaCases] = useState(false);
  const [toggleSectionActive, setToggleSectionActive] = useState(() => {
    return location.state?.defaultTab === 'second' ? 'second' : 'first';
  });
  const { canViewOthers } = usePermissionsCalendar();

  const assignedUserId = canSelectUser
    ? (selectedUser?.backendId ??
      (selectedUser?.value ? Number(selectedUser.value) : null) ??
      normalizedUserId ??
      null)
    : normalizedUserId;

  useEffect(() => {
    calendarI18n.changeLanguage(calendarLanguage);
  }, [calendarLanguage]);

  useEffect(() => {
    if (!canSelectUser) return;

    const setDefaultUser = (users) => {
      if (selectedUser || normalizedUserId === null) return;
      const defaultUser = users.find(
        (user) => Number(user.backendId ?? user.value) === normalizedUserId
      );
      if (defaultUser) {
        setSelectedUser(defaultUser);
      }
    };

    if (allUserOptions.length > 0) {
      setDefaultUser(allUserOptions);
      return;
    }

    const loadUsers = async () => {
      try {
        const users = await fetchAllSystemUsers();
        setAllUserOptions(users);
        setDefaultUser(users);
      } catch (error) {
        console.error('Error loading users for calendar:', error);
      }
    };

    loadUsers();
  }, [canSelectUser, normalizedUserId, allUserOptions, selectedUser]);

  useEffect(() => {
    const loadTasksForMonth = async () => {
      try {
        setLoadingTasks(true);

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        const start = new Date(year, month, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(year, month + 1, 0);
        end.setHours(23, 59, 59, 999);

        const formatDateOnly = (date) => {
          if (!date) return null;
          const d = new Date(date);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const da = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${da}`;
        };

        const monthlyParams = {
          usuarioAsignadoId: assignedUserId ?? null,
          statusId: 2,
          fechaDesde: formatDateOnly(start),
          fechaHasta: formatDateOnly(end),
        };
        const items = await fetchCalendarTasks(monthlyParams);

        const mappedTasks = (items || []).map((item) => {
          const dateValue = item.fechaVencimiento;
          const dateObj = dateValue ? new Date(dateValue) : null;

          let time = null;
          if (
            dateObj &&
            (dateObj.getHours() !== 0 || dateObj.getMinutes() !== 0)
          ) {
            time = dateObj.toLocaleTimeString('es-CL', {
              hour: '2-digit',
              minute: '2-digit',
            });
          }

          return {
            id: item.id,
            date: dateObj,
            title: item.nombreTarea,
            courtName: item.courtName,
            priority: mapPriority(item.priority),
            time,
            code: item.casoRol,
            clientName: item.clientName || null,
            assigneeName: item.assigneeName || null,
            status: item.nombreEstado || item.status,
            caseId: item.caseId,
            taskId: item.id,
            isActive: item.isActive ?? true,
            internalStateStatus:
              item.internalStateStatus ?? item.caseInternalStateStatus ?? null,
            blockManagerValue:
              item.blockManagerValue ??
              item.blockManager ??
              item.caseBlockManager ??
              item.internalState?.blockManager ??
              false,
          };
        });

        setTasks(mappedTasks);
      } catch (error) {
        console.error('Error loading tasks for calendar:', error);
      } finally {
        setLoadingTasks(false);
      }
    };

    loadTasksForMonth();
  }, [currentMonth, assignedUserId]);

  const tasksByDate = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      const statusText = String(task.internalStateStatus ?? '').toLowerCase();
      const isBaja = task.blockManagerValue === true;
      const isInactive =
        !isBaja &&
        (statusText.includes('inactiva') || statusText.includes('inactivo'));

      if (isInactive && !showInactiveCases) return;
      if (isBaja && !showBajaCases) return;
      const key = buildDateKey(task.date);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return map;
  }, [tasks, showInactiveCases, showBajaCases]);

  const appointmentsByDate = useMemo(() => {
    const map = {};

    appointments.forEach((appointment) => {
      const start = new Date(appointment.startAtUtc + 'Z');
      const key = buildDateKey(start);

      if (!map[key]) map[key] = [];
      map[key].push(appointment);
    });

    return map;
  }, [appointments]);

  const tasksForSelectedDate = tasksByDate[buildDateKey(selectedDate)] || [];
  const handleDayClick = (date) => {
    setSelectedDate(date);
  };

  const handleMonthChange = (date) => {
    setCurrentMonth(date);
  };

  const appointmentsForSelectedDate =
    appointmentsByDate[buildDateKey(selectedDate)]?.map((a) => {
      const start = new Date(`${a.startAtUtc}Z`);
      const end = new Date(`${a.endAtUtc}Z`);

      const formatDate = (date) =>
        date.toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

      const formatTime = (date) =>
        date.toLocaleTimeString('es-CL', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

      return {
        id: a.id,
        title: a.title,
        startDateTime: `${formatDate(start)} - ${formatTime(start)}`,
        endDateTime: `${formatDate(end)} -  ${formatTime(end)}`,
        userName: a.userName,
        origin: a.origin,
        description: a.description,
      };
    }) || [];

  const loadAppointmentsForMonth = async (monthDate = currentMonth) => {
    try {
      setLoadingAppointments(true);

      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();

      const start = new Date(year, month, 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(year, month + 1, 0);
      end.setHours(23, 59, 59, 999);

      const fromUtc = start.toISOString();
      const toUtc = end.toISOString();

      const data = await getAppointmentRange.getRange({
        fromUtc,
        toUtc,
        onlyActive: true,
      });

      setAppointments(data || []);
    } catch (error) {
      console.error(error.customMessage || error.message);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleAppointmentCreated = async () => {
    if (toggleSectionActive !== 'second') {
      setToggleSectionActive('second');
    }

    await loadAppointmentsForMonth();
  };

  useEffect(() => {
    if (toggleSectionActive !== 'second') return;
    loadAppointmentsForMonth();
  }, [currentMonth, toggleSectionActive]);

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-3'>
        <div className='d-flex justify-content-between w-100'>
          <h2 className=' fw-semibold'>{t('calendar_title')}</h2>
          <>
            <div className='btn-group' role='group'>
              <Button
                label={t('calendar_tab_tasks')}
                variant={`${toggleSectionActive === 'first' ? 'primary' : 'secondary'}`}
                onClick={() => {
                  setToggleSectionActive('first');
                }}
                className='btn-group-individual'
                size='sm'
              />
              <Button
                label={t('calendar_tab_schedule')}
                variant={`${toggleSectionActive === 'second' ? 'primary' : 'secondary'}`}
                onClick={() => {
                  setToggleSectionActive('second');
                }}
                className='btn-group-individual'
                size='sm'
              />
            </div>
          </>
        </div>
      </div>
      {toggleSectionActive === 'first' && (
        <>
          <div className='row mt-3'>
            <div className='col-12 col-lg-12'>
              <div className='calendar__user-filter'>
                <ComboBox
                  label={t('calendar_user_label')}
                  placeholder={t('calendar_user_placeholder')}
                  options={allUserOptions}
                  value={selectedUser}
                  onChange={(selected) => setSelectedUser(selected)}
                  disabled={!canViewOthers}
                />
                <div className='filter-group d-flex flex-row gap-4 py-3'>
                  <div className='calendar-filter'>
                    <div className='calendar-filter__label'>
                      <span className='caption'>{t('calendar_filter_inactive_label')}</span>
                      <span className='calendar-filter__help-wrapper'>
                        <span aria-label={t('calendar_filter_inactive_aria')} role='img'>
                          {SvgIcon ? (
                            <SvgIcon
                              name='questionCircle'
                              size='14px'
                              fill='var(--primary-500)'
                            />
                          ) : (
                            '?'
                          )}
                        </span>
                        <span className='calendar-filter__tooltip'>
                          <span className='calendar-filter__tooltip-title'>
                            {t('calendar_filter_inactive_tooltip_title')}
                          </span>
                          <span className='calendar-filter__tooltip-text'>
                            {t('calendar_filter_inactive_tooltip_text')}
                          </span>
                        </span>
                      </span>
                    </div>
                    <ActiveInactiveSwitch
                      value={showInactiveCases}
                      onChange={setShowInactiveCases}
                      activeLabel={t('calendar_switch_yes')}
                      inactiveLabel={t('calendar_switch_no')}
                    />
                  </div>
                  <div className='calendar-filter'>
                    <div className='calendar-filter__label'>
                      <span className='caption'>{t('calendar_filter_baja_label')}</span>
                      <span className='calendar-filter__help-wrapper'>
                        <span aria-label={t('calendar_filter_baja_aria')} role='img'>
                          {SvgIcon ? (
                            <SvgIcon
                              name='questionCircle'
                              size='14px'
                              fill='var(--primary-500)'
                            />
                          ) : (
                            '?'
                          )}
                        </span>
                        <span className='calendar-filter__tooltip'>
                          <span className='calendar-filter__tooltip-title'>
                            {t('calendar_filter_baja_tooltip_title')}
                          </span>
                          <span className='calendar-filter__tooltip-text'>
                            {t('calendar_filter_baja_tooltip_text')}
                          </span>
                        </span>
                      </span>
                    </div>
                    <ActiveInactiveSwitch
                      value={showBajaCases}
                      onChange={setShowBajaCases}
                      activeLabel={t('calendar_switch_yes')}
                      inactiveLabel={t('calendar_switch_no')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-12 col-lg-9 mb-4 mb-lg-0'>
              <Card>
                <Calendar
                  DesignSystemComponents={DesignSystemComponents}
                  selectedDate={selectedDate}
                  onDayClick={handleDayClick}
                  onMonthChange={handleMonthChange}
                  mode='tasks'
                  renderDayContent={(date) => {
                    const key = buildDateKey(date);
                    const dayTasks = tasksByDate[key] || [];
                    if (!dayTasks.length) return null;

                    return dayTasks.map((task) => (
                      <CalendarItem
                        key={task.id}
                        title={task.title}
                        courtName={task.courtName}
                        priority={task.priority}
                        onClick={task.onOpenTask}
                      />
                    ));
                  }}
                />
              </Card>
            </div>
            <div className='col-12 col-lg-3'>
              <CalendarSidebar
                DesignSystemComponents={DesignSystemComponents}
                selectedDate={selectedDate}
                items={tasksForSelectedDate}
                loading={loadingTasks}
                mode='tasks'
              />
            </div>
          </div>
        </>
      )}
      {toggleSectionActive === 'second' && (
        <div className='row mt-3'>
          <div className='col-12 col-lg-9 mb-4 mb-lg-0'>
            <Card>
              <Calendar
                DesignSystemComponents={DesignSystemComponents}
                selectedDate={selectedDate}
                onDayClick={handleDayClick}
                onMonthChange={handleMonthChange}
                mode='appointments'
                onAppointmentCreated={handleAppointmentCreated}
                renderDayContent={(date) => {
                  const key = buildDateKey(date);
                  const dayAppointments = appointmentsByDate[key] || [];

                  return dayAppointments.map((appointment) => {
                    const start = new Date(`${appointment.startAtUtc}Z`);

                    const time = start.toLocaleTimeString('es-CL', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    });

                    return (
                      <CalendarItem
                        key={appointment.id}
                        title={`${time} - ${appointment.origin === 'outlook' ? 'Outlook' : appointment.title}`}
                        priority='none'
                      />
                    );
                  });
                }}
              />
            </Card>
          </div>
          <div className='col-12 col-lg-3'>
            <CalendarSidebar
              DesignSystemComponents={DesignSystemComponents}
              selectedDate={selectedDate}
              items={appointmentsForSelectedDate}
              loading={loadingAppointments}
              mode='appointments'
              onAppointmentUpdated={handleAppointmentCreated}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
